import { baseApi } from '@/store/baseApi';

import type {
  ChatConversation,
  ChatDestinationFilters,
  ChatMessage,
  ChatMessageFilters,
  CreateChatMessagePayload,
  RealtimeTicket,
  MarkChatConversationReadPayload,
  OpenDirectMessagePayload,
  UpdateChatMessagePayload,
} from '@/features/chat/types';
import type { APIResponse, PaginatedData } from '@web/types';

export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getChatDestinations: builder.query<
      APIResponse<PaginatedData<ChatConversation>>,
      ChatDestinationFilters | void
    >({
      query: (params) => ({
        url: 'chat/destinations',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ChatDestinations', id: 'LIST' }],
    }),

    getGeneralChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      ChatMessageFilters | void
    >({
      query: (params) => ({
        url: 'chat/general',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ChatMessages', id: 'GENERAL' }],
    }),

    getStreamChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      ChatMessageFilters | void
    >({
      query: (params) => ({
        url: 'chat/stream',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ChatMessages', id: 'STREAM' }],
    }),

    getUnreadChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      ChatMessageFilters | void
    >({
      query: (params) => ({
        url: 'chat/unread',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ChatMessages', id: 'UNREAD' }],
    }),

    getMentionChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      ChatMessageFilters | void
    >({
      query: (params) => ({
        url: 'chat/mentions',
        method: 'GET',
        params: params ?? undefined,
      }),
      providesTags: [{ type: 'ChatMessages', id: 'MENTIONS' }],
    }),

    createGeneralChatMessage: builder.mutation<
      APIResponse<ChatMessage>,
      CreateChatMessagePayload
    >({
      query: (body) => ({
        url: 'chat/general',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'ChatMessages', id: 'GENERAL' },
        { type: 'ChatMessages', id: 'STREAM' },
        { type: 'ChatDestinations', id: 'LIST' },
      ],
    }),

    getJobChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      { jobId: string; params?: ChatMessageFilters }
    >({
      query: ({ jobId, params }) => ({
        url: `chat/jobs/${jobId}`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { jobId }) => [
        { type: 'ChatMessages', id: `JOB:${jobId}` },
      ],
    }),

    createJobChatMessage: builder.mutation<
      APIResponse<ChatMessage>,
      { jobId: string; payload: CreateChatMessagePayload }
    >({
      query: ({ jobId, payload }) => ({
        url: `chat/jobs/${jobId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'ChatMessages', id: `JOB:${jobId}` },
        { type: 'ChatMessages', id: 'STREAM' },
        { type: 'ChatDestinations', id: 'LIST' },
      ],
    }),

    openDirectMessage: builder.mutation<
      APIResponse<ChatConversation>,
      OpenDirectMessagePayload
    >({
      query: (body) => ({
        url: 'chat/direct-messages',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'ChatDestinations', id: 'LIST' }],
    }),

    createDirectChatMessage: builder.mutation<
      APIResponse<ChatMessage>,
      { conversationId: string; payload: CreateChatMessagePayload }
    >({
      query: ({ conversationId, payload }) => ({
        url: `chat/direct-messages/${conversationId}/messages`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, { conversationId }) => [
        { type: 'ChatMessages', id: `DM:${conversationId}` },
        { type: 'ChatDestinations', id: 'LIST' },
      ],
    }),

    getDirectChatMessages: builder.query<
      APIResponse<PaginatedData<ChatMessage>>,
      { conversationId: string; params?: ChatMessageFilters }
    >({
      query: ({ conversationId, params }) => ({
        url: `chat/direct-messages/${conversationId}/messages`,
        method: 'GET',
        params,
      }),
      providesTags: (_result, _error, { conversationId }) => [
        { type: 'ChatMessages', id: `DM:${conversationId}` },
      ],
    }),

    getChatMessage: builder.query<APIResponse<ChatMessage>, string>({
      query: (messageId) => ({
        url: `chat/messages/${messageId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, messageId) => [
        { type: 'ChatMessages', id: `MESSAGE:${messageId}` },
      ],
    }),

    updateChatMessage: builder.mutation<
      APIResponse<ChatMessage>,
      { messageId: string; payload: UpdateChatMessagePayload }
    >({
      query: ({ messageId, payload }) => ({
        url: `chat/messages/${messageId}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'ChatMessages', id: 'GENERAL' },
        { type: 'ChatMessages', id: 'STREAM' },
        { type: 'ChatMessages', id: 'UNREAD' },
        { type: 'ChatMessages', id: 'MENTIONS' },
      ],
    }),

    deleteChatMessage: builder.mutation<
      APIResponse<ChatMessage>,
      { messageId: string }
    >({
      query: ({ messageId }) => ({
        url: `chat/messages/${messageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'ChatMessages', id: 'GENERAL' },
        { type: 'ChatMessages', id: 'STREAM' },
        { type: 'ChatDestinations', id: 'LIST' },
      ],
    }),

    markChatConversationRead: builder.mutation<
      APIResponse<null>,
      { conversationId: string; payload?: MarkChatConversationReadPayload }
    >({
      query: ({ conversationId, payload }) => ({
        url: `chat/conversations/${conversationId}/read`,
        method: 'POST',
        body: payload ?? {},
      }),
      invalidatesTags: [
        { type: 'ChatConversationReadState', id: 'CURRENT' },
        { type: 'ChatDestinations', id: 'LIST' },
      ],
    }),

    createRealtimeTicket: builder.mutation<APIResponse<RealtimeTicket>, void>({
      query: () => ({
        url: 'realtime/tickets',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetChatDestinationsQuery,
  useGetGeneralChatMessagesQuery,
  useLazyGetGeneralChatMessagesQuery,
  useGetStreamChatMessagesQuery,
  useGetUnreadChatMessagesQuery,
  useGetMentionChatMessagesQuery,
  useCreateGeneralChatMessageMutation,
  useGetJobChatMessagesQuery,
  useLazyGetJobChatMessagesQuery,
  useCreateJobChatMessageMutation,
  useOpenDirectMessageMutation,
  useGetDirectChatMessagesQuery,
  useLazyGetDirectChatMessagesQuery,
  useCreateDirectChatMessageMutation,
  useLazyGetChatMessageQuery,
  useUpdateChatMessageMutation,
  useDeleteChatMessageMutation,
  useMarkChatConversationReadMutation,
  useCreateRealtimeTicketMutation,
} = chatApi;
